import React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { QC_ISSUE_CATEGORY_LABELS } from '../../../interfaces/qcIssue.interface';
import { QC_CATEGORY_RGB, WARN_ORANGE } from './qcTheme';
import { QcSummaryRow } from './workspaceDerivations';
import {
  CategoryChip,
  CloseButton,
  LinkButton,
  ModalHeader,
  ModalTitle,
  PrimaryButton,
  QuestionCode,
  SectionCaption,
} from './workspaceModalParts';

const Body = styled('div')({
  padding: '22px 24px',
});

const WarningBanner = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.12)',
  borderLeft: `4px solid ${WARN_ORANGE}`,
  borderRadius: '4px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'hidden',
});

const WarningIconCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  width: '64px',
  background: 'rgba(230,81,0,0.06)',
});

const WarningBody = styled('div')({
  minWidth: 0,
  padding: '15px 18px',
});

const WarningEyebrow = styled('div')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 700,
  color: WARN_ORANGE,
  textTransform: 'uppercase',
});

const WarningText = styled('p')({
  margin: '6px 0 0',
  fontSize: '0.9rem',
  lineHeight: 1.6,
  color: 'rgba(0,0,0,0.8)',
});

const SummarySection = styled('div')({
  marginTop: '20px',
});

const SummaryHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '8px',
});

const SummaryCountLabel = styled('span')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.55)',
});

const SummaryTable = styled('div')({
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '6px',
  overflow: 'hidden',
  background: '#fff',
});

const SummaryHeaderRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: '92px 158px minmax(0,1fr)',
  columnGap: '16px',
  alignItems: 'center',
  padding: '9px 14px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
});

const HeaderCell = styled('span')({
  fontSize: '0.72rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.6)',
});

const SummaryScroll = styled('div')({
  overflow: 'auto',
  maxHeight: '340px',
});

const SummaryRowButton = styled('button')({
  display: 'grid',
  gridTemplateColumns: '92px 158px minmax(0,1fr)',
  columnGap: '16px',
  alignItems: 'start',
  width: '100%',
  padding: '11px 14px',
  border: 'none',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const CodeCell = styled('span')({
  display: 'grid',
  gridTemplateColumns: '18px auto',
  columnGap: '5px',
  alignItems: 'center',
});

const RosetteSlot = styled('span')({
  display: 'flex',
  justifyContent: 'center',
});

const IssueTextCell = styled('span')({
  fontSize: '0.82rem',
  lineHeight: 1.45,
  color: 'rgba(0,0,0,0.65)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minWidth: 0,
});

const Footer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '16px 24px 22px',
  borderTop: '1px solid rgba(0,0,0,0.1)',
});

export interface SubmitQcIssuesModalProps {
  open: boolean;
  rows: QcSummaryRow[];
  onGoToQuestion: (row: QcSummaryRow) => void;
  onConfirm: () => void;
  onClose: () => void;
}

export default function SubmitQcIssuesModal({ open, rows, onGoToQuestion, onConfirm, onClose }: SubmitQcIssuesModalProps): JSX.Element {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='Confirm QC Issues Submission'
      PaperProps={{ sx: { width: 1040, maxWidth: 'calc(100% - 32px)', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <ModalHeader>
        <ModalTitle>Confirm QC Issues Submission</ModalTitle>
        <CloseButton type='button' aria-label='Close submission confirmation' onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </ModalHeader>
      <Body>
        <WarningBanner>
          <WarningIconCell>
            <ReportProblemIcon sx={{ fontSize: 26, color: WARN_ORANGE }} />
          </WarningIconCell>
          <WarningBody>
            <WarningEyebrow>This publishes your QC review</WarningEyebrow>
            <WarningText>
              Confirm your QC review is complete. Once submitted, the assessor is notified and
              <strong> Round 1 begins</strong>. You can still add QC issues afterward — a new
              issue publishes immediately and joins the round in progress.
            </WarningText>
          </WarningBody>
        </WarningBanner>
        {rows.length > 0 && (
          <SummarySection>
            <SummaryHead>
              <SectionCaption>QC Issue Summary</SectionCaption>
              <SummaryCountLabel>{rows.length} issue{rows.length === 1 ? '' : 's'}</SummaryCountLabel>
            </SummaryHead>
            <SummaryTable>
              <SummaryHeaderRow>
                <HeaderCell>Question</HeaderCell>
                <HeaderCell>Category</HeaderCell>
                <HeaderCell>QC Issue</HeaderCell>
              </SummaryHeaderRow>
              <SummaryScroll>
                {rows.map(row => (
                  <SummaryRowButton key={row.issue.id} type='button' onClick={() => onGoToQuestion(row)}>
                    <CodeCell>
                      <RosetteSlot>
                        {row.entry.question.isBestPractice && (
                          <WorkspacePremiumIcon titleAccess='Best Practice question' sx={{ fontSize: 15, color: '#D467E4' }} />
                        )}
                      </RosetteSlot>
                      <QuestionCode>{row.entry.question.shortCode}</QuestionCode>
                    </CodeCell>
                    <CategoryChip rgb={QC_CATEGORY_RGB[row.issue.category]}>
                      {QC_ISSUE_CATEGORY_LABELS[row.issue.category]}
                    </CategoryChip>
                    <IssueTextCell title={row.issue.text}>{row.issue.text}</IssueTextCell>
                  </SummaryRowButton>
                ))}
              </SummaryScroll>
            </SummaryTable>
          </SummarySection>
        )}
      </Body>
      <Footer>
        <LinkButton type='button' onClick={onClose}>
          <ArrowBackIcon sx={{ fontSize: 17 }} />
          Go Back and Review
        </LinkButton>
        <PrimaryButton type='button' onClick={onConfirm}>Confirm</PrimaryButton>
      </Footer>
    </Dialog>
  );
}
